import { z } from 'zod'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { embedOriginSchema, httpsUrlSchema } from '../validators/tenant.validator.js'

/** How many sites one mode's signing page may be embedded on. */
export const EMBED_ORIGINS_MAX = 10

/** A new API key, live or test. Owners create live keys; owners and developers create test keys. */
export const createApiKeyInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the key a name').max(60, 'Use at most 60 characters'),
    mode: tenantModeSchema,
  })
  .strict()

export type CreateApiKeyInput = z.infer<typeof createApiKeyInputSchema>

/**
 * One mode's integration settings. `webhookUrl: null` stops webhook delivery.
 * Local development origins are accepted here and refused for live by the API.
 */
export const updateModeSettingsInputSchema = z
  .object({
    webhookUrl: httpsUrlSchema.nullable(),
    embedOrigins: z
      .array(embedOriginSchema)
      .max(EMBED_ORIGINS_MAX, `Allow at most ${String(EMBED_ORIGINS_MAX)} origins`)
      .transform((origins) => [...new Set(origins)]),
  })
  .strict()

export type UpdateModeSettingsInput = z.infer<typeof updateModeSettingsInputSchema>

/** How long the old webhook secret keeps signing deliveries after a rotation. */
export const WEBHOOK_SECRET_OVERLAPS = ['none', '24h', '7d'] as const

export const webhookSecretOverlapSchema = z.enum(WEBHOOK_SECRET_OVERLAPS)

export type WebhookSecretOverlap = z.infer<typeof webhookSecretOverlapSchema>

export const WEBHOOK_SECRET_OVERLAP_HOURS: Record<WebhookSecretOverlap, number> = {
  none: 0,
  '24h': 24,
  '7d': 7 * 24,
}

/**
 * A new webhook secret. With an overlap, deliveries are signed with both the
 * new and the old secret until it ends, so a receiver can switch without
 * rejecting anything.
 */
export const rotateWebhookSecretInputSchema = z
  .object({
    overlap: webhookSecretOverlapSchema,
  })
  .strict()

export type RotateWebhookSecretInput = z.infer<typeof rotateWebhookSecretInputSchema>
